import { z } from 'zod';
import { email, indianPhone, objectId, otp, password } from './common.js';
import { ALL_ROLES } from '../enums/roles.js';
/** Step 1 login: creds. */
export const loginInput = z.object({
    email,
    password: z.string().min(1),
});
/** Step 2 login: workspace pick. */
export const workspacePickInput = z.object({
    userId: objectId,
    workspaceId: objectId,
});
/**
 * Multi-tenant refactor (PR 2) — student in the workspace switcher hands us the tenantId
 * they want to activate. Server verifies the tenantId is one of their verified Memberships
 * before minting a fresh access token with `activeTenantId = tenantId`. Refresh token
 * unchanged across the switch.
 */
export const switchWorkspaceInput = z.object({
    tenantId: objectId,
});
/** Student self-serve register — final submit.
 *
 * PR 4a extends this to support the three onboarding paths (Path 1 CSV shell activation,
 * Path 2 self-declared + TPO manual approve, Path 3 roster-match auto-verify).
 * `collegeId` is legacy (single-tenant era); prefer `declaredTenantId` for new writes.
 * `graduationYear` is deprecated in favor of `joiningYear` — server computes current-year
 * at read time so joining year stays authoritative across the student's career.
 */
export const studentRegisterInput = z.object({
    fullName: z.string().min(2).max(120),
    email,
    phone: indianPhone,
    password,
    // PR 4a — student picks from the tenant picker (either an on-Kollez Tenant, or an
    // AISHECollege via `aisheCollegeId` fallback). Both optional so unaffiliated (Persona 1)
    // signups still work.
    declaredTenantId: objectId.optional(),
    aisheCollegeId: objectId.optional(),
    // Legacy — kept for backwards compat during the transition; server prefers declaredTenantId.
    collegeId: objectId.optional(),
    program: z.string().min(1),
    // PR 4a — extend from single `branch` to explicit specialization. `branch` kept for compat.
    branch: z.string().min(1),
    specialization: z.string().min(1).optional(),
    // PR 4a — store joining year (authoritative), derive current-year at read time. Deprecates
    // graduationYear which was misleading (implies future date).
    joiningYear: z.number().int().min(2015).max(new Date().getFullYear() + 1).optional(),
    graduationYear: z.number().int().min(2024).max(2035).optional(),
    rollNumber: z.string().min(1).max(30).optional(),
    // PR 4a — the ONE field student types for Path 3 roster match. Server accepts any of
    // { rollNumber, email, mobile } — comparison is scoped to declaredTenantId.
    uniqueIdentifierForShellMatch: z.string().min(1).max(120).optional(),
    cgpa: z.number().min(0).max(10).optional(),
    backlogs: z.number().int().min(0).max(50).optional(),
    class10: z.number().min(0).max(100).optional(),
    class12: z.number().min(0).max(100).optional(),
    skills: z.array(z.string()).max(50).default([]),
    interestedRoles: z.array(z.string()).max(20).default([]),
    preferredLocations: z.array(z.string()).max(20).default([]),
    consent: z.literal(true, {
        errorMap: () => ({ message: 'Consent is required by DPDP Act' }),
    }),
    // Step 100 — DPDP §6 parental consent (Decision 8, v1.5). YYYY-MM-DD; server
    // computes age vs signup date and requires parentGuardian when under 18.
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'DOB must be YYYY-MM-DD').optional(),
    parentGuardian: z.object({
        mobile: indianPhone.optional(),
        name: z.string().max(120).optional(),
        email: email.optional(),
    }).optional(),
}).superRefine((val, ctx) => {
    // If dateOfBirth indicates the student is under 18 as of the signup timestamp,
    // parentGuardian.mobile is REQUIRED. Otherwise both fields are optional. This is
    // enforced at the schema layer so no controller can accept a minor without a
    // parent contact.
    if (!val.dateOfBirth)
        return;
    const dobMs = new Date(val.dateOfBirth).getTime();
    if (!Number.isFinite(dobMs))
        return;
    const ageYears = (Date.now() - dobMs) / (365.2425 * 24 * 60 * 60 * 1000);
    if (ageYears < 18 && !val.parentGuardian?.mobile) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parentGuardian', 'mobile'],
            message: "You're under 18, so we need a parent or guardian's mobile number to comply with DPDP Act §6.",
        });
    }
});
/** Institution/company sales lead (no direct account creation). */
export const institutionLeadInput = z.object({
    name: z.string().min(2),
    role: z.enum(ALL_ROLES),
    email,
    phone: indianPhone.optional(),
    org: z.string().min(2),
    kind: z.enum(['college', 'university', 'group', 'company']),
    size: z.string().optional(),
    note: z.string().max(2000).optional(),
});
/** OTP flows. */
export const otpSendInput = z.object({
    email: email.optional(),
    phone: indianPhone.optional(),
    method: z.enum(['email', 'sms']),
});
export const otpVerifyInput = z.object({
    email: email.optional(),
    phone: indianPhone.optional(),
    method: z.enum(['email', 'sms']),
    otp,
});
/** Forgot password. */
export const forgotStartInput = z.object({
    identifier: z.union([email, indianPhone]),
    channel: z.enum(['email', 'sms']),
});
export const forgotResetInput = z.object({
    token: z.string().min(10),
    newPassword: password,
});
/** Refresh. */
export const refreshInput = z.object({ refreshToken: z.string().min(10) });
/** Change password (authed). */
export const changePasswordInput = z.object({
    currentPassword: z.string().min(1),
    newPassword: password,
});
/**
 * PR 4a — Path 3 claim OTP verify. When a student's registration matches an imported shell,
 * the server sends OTPs to the ROSTER's email + mobile (never the student's typed contacts).
 * Student verifies both here; on pass, the shell is claimed atomically.
 */
export const claimOtpVerifyInput = z.object({
    claimAttemptId: z.string().min(8).max(64),
    emailOtp: otp,
    mobileOtp: otp,
});
//# sourceMappingURL=auth.js.map